/**
 * GoCardless Mandate Completion Page
 *
 * Handles the redirect-back from GoCardless after SEPA mandate authorization.
 * Reads redirect_flow_id from URL params, calls completion API, shows result.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Building2, Check, Loader2 } from 'lucide-react';
import { useCompleteGoCardlessMandate } from '../hooks/usePaymentMandates';

type CompletionState = 'loading' | 'success' | 'error';

export const GoCardlessMandateComplete: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectFlowId = searchParams.get('redirect_flow_id');

  const [state, setState] = useState<CompletionState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const completeMandate = useCompleteGoCardlessMandate();

  useEffect(() => {
    if (!redirectFlowId) {
      setState('error');
      setErrorMessage(t('payments.gocardless.mandateComplete.missingRedirectFlow'));
      return;
    }

    completeMandate.mutate(redirectFlowId, {
      onSuccess: (mandate: any) => {
        setState('success');
        // Store new mandate ID for the form to pick up
        sessionStorage.setItem('newMandateId', mandate.id);
      },
      onError: (err: any) => {
        setState('error');
        setErrorMessage(
          err?.message || t('payments.gocardless.mandateComplete.defaultError')
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectFlowId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 max-w-md w-full text-center">
        {state === 'loading' && (
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('payments.gocardless.mandateComplete.finalizingTitle')}
            </h2>
            <p className="text-gray-500">
              {t('payments.gocardless.mandateComplete.verifyingAuth')}
            </p>
          </div>
        )}

        {state === 'success' && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center animate-scale-in">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('payments.gocardless.mandateComplete.mandateActivated')}
            </h2>
            <p className="text-gray-500">
              {t('payments.gocardless.mandateComplete.mandateActivatedDescription')}
            </p>
            <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 rounded-xl mx-auto w-fit">
              <Building2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">{t('payments.gocardless.mandateComplete.goCardlessSEPA')}</span>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('payments.gocardless.mandateComplete.backToForm')}
            </button>
          </div>
        )}

        {state === 'error' && (
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('payments.gocardless.mandateComplete.configError')}
            </h2>
            <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">
              {errorMessage}
            </p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                {t('payments.gocardless.mandateComplete.back')}
              </button>
              {redirectFlowId && (
                <button
                  onClick={() => {
                    setState('loading');
                    setErrorMessage('');
                    completeMandate.mutate(redirectFlowId, {
                      onSuccess: (mandate: any) => {
                        setState('success');
                        sessionStorage.setItem('newMandateId', mandate.id);
                      },
                      onError: (err: any) => {
                        setState('error');
                        setErrorMessage(
                          err?.message || t('payments.gocardless.mandateComplete.finalizationError')
                        );
                      },
                    });
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors"
                >
                  {t('payments.gocardless.mandateComplete.retry')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoCardlessMandateComplete;
